import moment from "moment-timezone";

/*
 * A party moves as one: dropping one of its seats moves the others too once
 * the move is confirmed. While the drag is on, the others follow it on screen,
 * shifted by the same time and the same number of rows. Where they land is a
 * preview: the seating strategy picks the actual seats, which the
 * confirmation lists.
 */

// A row a seat can be shown on: not a tag's header, the Unassigned row or an
// expense row (the rows handleItemMove refuses as a drop target).
const isSeatRow = (row) =>
  Boolean(row) &&
  !row.root &&
  Boolean(row.itemProps?.targetId) &&
  row.itemProps.mode !== "expenses";

// How far the dragged block is from where it started: in ms, and in rows on
// screen (the row index the timeline reports while dragging counts those).
export const dragShift = (item, time, rowIndex, rows) => {
  const fromRow = rows.findIndex((row) => row.id === item.group);
  return {
    timeShift: time - moment(item.start_time).valueOf(),
    rowShift: fromRow < 0 ? 0 : rowIndex - fromRow,
  };
};

// The items with the dragged block's party shifted along with it. The
// dragged block itself is left to the timeline, which draws it under the
// pointer; a seat whose row would fall off the seats keeps its row.
export const followDrag = (items, drag, rows) => {
  if (!drag || (!drag.timeShift && !drag.rowShift)) return items;
  return items.map((item) => {
    if (
      item.id === drag.itemId ||
      !item.itemProps?.partyKey ||
      item.itemProps.partyKey !== drag.partyKey
    ) {
      return item;
    }
    const fromRow = rows.findIndex((row) => row.id === item.group);
    const toRow = fromRow < 0 ? undefined : rows[fromRow + drag.rowShift];
    return {
      ...item,
      group: isSeatRow(toRow) ? toRow.id : item.group,
      start_time: moment(item.start_time).add(drag.timeShift, "ms"),
      end_time: moment(item.end_time).add(drag.timeShift, "ms"),
      following: true,
    };
  });
};

// Where each seat of a dragged time block lands, for the CMS (move-block):
// the seats move by the same rows as the preview, the dragged one included,
// and a seat whose new row would fall off the seats keeps its own. Null when
// two seats would land on the same one.
export const blockSeats = (items, drag, rows) => {
  const seats = items
    .filter((item) => item.itemProps?.partyKey === drag.partyKey)
    .map((item) => {
      const fromRow = rows.findIndex((row) => row.id === item.group);
      const toRow = fromRow < 0 ? undefined : rows[fromRow + drag.rowShift];
      const row = isSeatRow(toRow) ? toRow : rows[fromRow];
      return {
        timespanId: item.itemProps.targetId,
        resourceId: row?.itemProps?.targetId,
      };
    })
    .filter((seat) => seat.timespanId && seat.resourceId);
  const resources = new Set(seats.map((seat) => seat.resourceId));
  return resources.size === seats.length ? seats : null;
};
